import React, { useMemo } from "react";
import styled from "styled-components";
import { breakpoints } from "../styles/variables";

interface StoererLink {
  linkUrl?: string;
  datei?: Array<{ url: string }>;
  telefon?: string;
  email?: string;
}

interface StoererProps {
  content: string;
  alignment?: string;
  link?: Array<StoererLink>;
  mobileContent?: string;
  variant?: "mobile" | "desktop";
}

const desktopBaseStyles = `
  display: none;

  @media screen and (min-width: ${breakpoints.desktop}px) {
    display: block;
    width: 100%;
    height: 100%;
    overflow: hidden;
    position: relative;

    .stoerer-background {
      width: 100%;
      height: 100%;
      display: block;
      background: url("/assets/gestaltungselemente/stoerer.svg");
      background-size: contain;
      background-position: center;
      background-repeat: no-repeat;
      cursor: default;
      text-decoration: none;
      pointer-events: auto;
      position: relative;
      overflow: hidden;

      > div {
        position: absolute;
        top: 48%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 65%;
        text-align: center;
        overflow: hidden;

        p,
        h2,
        h3,
        h4,
        h5,
        h6,
        li {
          color: #000 !important;
          font: 24px / 28px "FS Regular" !important;
          font-weight: 400 !important;
          overflow: hidden;
          text-overflow: ellipsis;
          display: -webkit-box;
          -webkit-line-clamp: 7;
          -webkit-box-orient: vertical;
          hanging-punctuation: first last allow-end;
          hyphens: auto;
          text-wrap: pretty;
        }

        em,
        i,
        strong,
        b {
          display: inline !important;
          color: #000 !important;
          font: 24px / 28px "FS Regular" !important;
          font-weight: 400 !important;
        }

        em,
        i {
          font-style: italic !important;
        }

        strong,
        b {
          font-weight: 600 !important;
        }

        a {
          color: #000 !important;
          font: 24px / 28px "FS Regular" !important;
        }
      }
    }

    &.link .stoerer-background {
      cursor: pointer;
      pointer-events: none;
    }
  }
`;

const DesktopStoererDiv = styled.div`
  ${desktopBaseStyles}
`;

const DesktopStoererLink = styled.a`
  ${desktopBaseStyles}
`;

const mobileBaseStyles = `
  display: block;
  width: 100%;
  padding: 9px 15px 11px;
  background: #f2d35b;
  text-align: center;
  cursor: default;
  text-decoration: none;
  pointer-events: none;

  > div {
    pointer-events: auto;
  }

  &.link {
    cursor: pointer;
    pointer-events: auto;

    > div {
      pointer-events: none;
    }
  }

  h2 {
    font: 20px / 24px "FS Medium";
    margin-bottom: 7px;
  }

  h3 {
    font: 16px / 21px "FS Regular";
  }

  @media screen and (min-width: ${breakpoints.desktop}px) {
    display: none;
  }
`;

const MobileStoererDiv = styled.div`
  ${mobileBaseStyles}
`;

const MobileStoererLink = styled.a`
  ${mobileBaseStyles}
`;

const Stoerer = ({ content, alignment, link, mobileContent, variant }: StoererProps) => {
  const stoererLink = useMemo(() => {
    if (!link?.length) return "";

    const linkItem = link[0];
    if (linkItem.linkUrl) return linkItem.linkUrl;
    if (linkItem.datei?.length) return linkItem.datei[0].url;
    if (linkItem.telefon) return `tel:${linkItem.telefon}`;
    if (linkItem.email) return `mailto:${linkItem.email}`;

    return "";
  }, [link]);

  const mobileText = mobileContent || content;
  const className =
    alignment == "obenRechts" && stoererLink
      ? "link right"
      : stoererLink
      ? "link"
      : "";

  const desktopInner = (
    <div className="stoerer-background">
      <div dangerouslySetInnerHTML={{ __html: content }} />
    </div>
  );

  const mobileInner = (
    <div dangerouslySetInnerHTML={{ __html: mobileText }} />
  );

  const showMobile = variant !== "desktop";
  const showDesktop = variant !== "mobile";

  return (
    <>
      {showMobile && (
        <>
          {stoererLink ? (
            <MobileStoererLink
              className="link"
              href={stoererLink}
              target="_blank"
            >
              {mobileInner}
            </MobileStoererLink>
          ) : (
            <MobileStoererDiv>{mobileInner}</MobileStoererDiv>
          )}
        </>
      )}
      {showDesktop && (
        <>
          {stoererLink ? (
            <DesktopStoererLink className={className} href={stoererLink} target="_blank">
              {desktopInner}
            </DesktopStoererLink>
          ) : (
            <DesktopStoererDiv className={className}>
              {desktopInner}
            </DesktopStoererDiv>
          )}
        </>
      )}
    </>
  );
};

export default Stoerer;
